import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { StateMachineState } from '../../ProgramStateMachine.types'
import { useStateMachineEditorContext } from '../../context/StateMachineEditorContext'
import { GroupEditForm } from '../GroupEditForm/GroupEditForm'
import { StateEditForm } from '../StateEditModal/StateEditForm'
import { TransitionListPanel } from '../TransitionEditModal/TransitionListPanel'

import { Flex, SidePanel, Tabs } from '@percent/lemonade'
import styles from './StateMachineSidePanel.module.scss'

type PanelTab = 'stage' | 'transition'

export interface StateMachineSidePanelProps {
  selectedState: StateMachineState | null
  isAddingState: boolean
  selectedGroup: string | null
  isAddingGroup: boolean
  onClose: () => void
}

export function StateMachineSidePanel({
  selectedState,
  isAddingState,
  selectedGroup,
  isAddingGroup,
  onClose
}: Readonly<StateMachineSidePanelProps>) {
  const { t } = useTranslation()
  const [panelTab, setPanelTab] = useState<PanelTab>('stage')

  const {
    localDefinition,
    addState,
    updateState,
    deleteState,
    addTransition,
    updateTransition,
    deleteTransition,
    updateGroupName,
    deleteGroup,
    updateGroupMembers
  } = useStateMachineEditorContext()

  const existingStateIds = useMemo(() => localDefinition?.states.map(s => s.id) ?? [], [localDefinition])

  const existingGroupNames = useMemo(() => {
    const names = new Set<string>()
    for (const s of localDefinition?.states ?? []) {
      if (s.metadata?.displayGroup) names.add(s.metadata.displayGroup)
    }

    return [...names]
  }, [localDefinition])

  const handleStateSubmit = useCallback(
    (state: StateMachineState) => {
      if (selectedState) {
        updateState(state)
      } else {
        addState(state)
      }
      onClose()
    },
    [selectedState, addState, updateState, onClose]
  )

  const handleStateDelete = useCallback(() => {
    if (selectedState) {
      deleteState(selectedState.id)
      onClose()
    }
  }, [selectedState, deleteState, onClose])

  const handleGroupSubmit = useCallback(
    (values: { name: string; memberIds: string[]; displayMode: string; tableType: string; previousName?: string }) => {
      if (values.previousName && values.previousName !== values.name) {
        updateGroupName(values.previousName, values.name)
      }
      updateGroupMembers(values.name, values.memberIds, values.displayMode, values.tableType)
      onClose()
    },
    [updateGroupName, updateGroupMembers, onClose]
  )

  const handleGroupDelete = useCallback(() => {
    if (selectedGroup) {
      deleteGroup(selectedGroup)
      onClose()
    }
  }, [selectedGroup, deleteGroup, onClose])

  const isGroupMode = isAddingGroup || selectedGroup !== null

  const panelTitle = isAddingGroup
    ? t('stateMachine.sidePanel.addGroup')
    : selectedGroup
    ? t('stateMachine.sidePanel.editGroup')
    : isAddingState
    ? t('stateMachine.sidePanel.addStage')
    : t('stateMachine.sidePanel.editStage')

  const panelTabItems = useMemo(
    () => [
      { key: 'stage', label: t('stateMachine.sidePanel.stageTab') },
      { key: 'transition', label: t('stateMachine.sidePanel.transitionTab') }
    ],
    [t]
  )

  const handleTabChange = useCallback((tab: string) => {
    setPanelTab(tab as PanelTab)
  }, [])

  return (
    <SidePanel
      title={panelTitle}
      onClose={onClose}
      testId="state-machine-side-panel"
      style={{ width: 420, minWidth: 420, borderLeft: '1px solid #e2e8f0', overflow: 'hidden' }}
    >
      <div className={styles.content}>
        {isGroupMode ? (
          <GroupEditForm
            allStates={localDefinition?.states ?? []}
            groupName={selectedGroup ?? undefined}
            existingGroupNames={existingGroupNames}
            onSubmit={handleGroupSubmit}
            onDelete={selectedGroup ? handleGroupDelete : undefined}
            onCancel={onClose}
          />
        ) : isAddingState ? (
          <StateEditForm onSubmit={handleStateSubmit} onCancel={onClose} existingStateIds={existingStateIds} />
        ) : (
          <Flex direction="column" flex={1} miw={0} mih={0}>
            <div style={{ flexShrink: 0 }}>
              <Tabs activeTab={panelTab} onChangeTab={handleTabChange} items={panelTabItems} />
            </div>
            {panelTab === 'stage' && (
              <StateEditForm
                onSubmit={handleStateSubmit}
                onDelete={selectedState ? handleStateDelete : undefined}
                onCancel={onClose}
                existingStateIds={existingStateIds}
                initialValues={selectedState ?? undefined}
              />
            )}
            {panelTab === 'transition' && selectedState && (
              <TransitionListPanel
                selectedState={selectedState}
                availableStates={localDefinition?.states ?? []}
                onAddTransition={addTransition}
                onUpdateTransition={updateTransition}
                onDeleteTransition={deleteTransition}
              />
            )}
          </Flex>
        )}
      </div>
    </SidePanel>
  )
}
